/**
 * Tracking Hooks
 * Manages insight tracking with TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { trackRepo } from '@/lib/repositories'
import { INSIGHTS_QUERY_KEY } from './useInsights'
import { analytics } from '@/lib/services/analytics'
import { AnalyticsEvents } from '@/lib/services/analytics-events'

// Query keys
export const TRACKED_IDS_KEY = ['tracked-insight-ids']
export const TRACKED_INSIGHTS_KEY = ['tracked-insights']
export const TRACK_COUNT_KEY = (insightId: string) => ['track-count', insightId]

/**
 * Hook to get all tracked insight IDs for current user
 * Used for checking if insights are tracked in the feed
 */
export function useTrackedIds() {
  return useQuery({
    queryKey: TRACKED_IDS_KEY,
    queryFn: () => trackRepo.getTrackedInsightIds(),
    staleTime: 30 * 1000, // 30 seconds
  })
}

/**
 * Hook to get all tracked insights with full details
 * Used for the tracked insights sheet
 */
export function useTrackedInsights() {
  return useQuery({
    queryKey: TRACKED_INSIGHTS_KEY,
    queryFn: () => trackRepo.getTrackedInsights(),
    staleTime: 30 * 1000,
  })
}

/**
 * Hook to get track count for a specific insight
 */
export function useTrackCount(insightId: string) {
  return useQuery({
    queryKey: TRACK_COUNT_KEY(insightId),
    queryFn: () => trackRepo.getTrackCount(insightId),
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Hook to track an insight
 */
export function useTrackInsight() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      insightId,
      priceAtTrack,
    }: {
      insightId: string
      priceAtTrack?: number
    }) => trackRepo.trackInsight(insightId, priceAtTrack),
    onSuccess: (_, { insightId }) => {
      queryClient.invalidateQueries({ queryKey: TRACKED_IDS_KEY })
      queryClient.invalidateQueries({ queryKey: TRACKED_INSIGHTS_KEY })
      queryClient.invalidateQueries({ queryKey: TRACK_COUNT_KEY(insightId) })
      queryClient.invalidateQueries({ queryKey: INSIGHTS_QUERY_KEY })
      analytics.capture(AnalyticsEvents.INSIGHT_TRACKED, { insight_id: insightId })
    },
  })
}

/**
 * Hook to untrack an insight
 */
export function useUntrackInsight() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (insightId: string) => trackRepo.untrackInsight(insightId),
    onSuccess: (_, insightId) => {
      queryClient.invalidateQueries({ queryKey: TRACKED_IDS_KEY })
      queryClient.invalidateQueries({ queryKey: TRACKED_INSIGHTS_KEY })
      queryClient.invalidateQueries({ queryKey: TRACK_COUNT_KEY(insightId) })
      queryClient.invalidateQueries({ queryKey: INSIGHTS_QUERY_KEY })
      analytics.capture(AnalyticsEvents.INSIGHT_UNTRACKED, { insight_id: insightId })
    },
  })
}

/**
 * Hook to update entry price for a tracked insight
 */
export function useUpdateEntryPrice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ insightId, price }: { insightId: string; price: number }) =>
      trackRepo.updateEntryPrice(insightId, price),
    onSuccess: () => {
      // Invalidate tracked insights to refresh the data
      queryClient.invalidateQueries({ queryKey: TRACKED_INSIGHTS_KEY })
    },
  })
}
